import { useCallback, useEffect, useRef, useState } from 'react'
import { API_URL, VERSION_OVERRIDE } from '../lib/api'

/**
 * Session workspace files (rt UI v2 spec §工作区 / F2).
 *
 * Read path (every role): GET /rt/join/:token/files — link-token auth, returns
 * presigned GET urls (15 min) for the Chromium built-in viewer iframe.
 * Write path (studio host, Better Auth HttpOnly cookie):
 *   POST /rt/sessions/:id/files/presign → PUT R2 → POST .../:fileId/complete
 * After a completed upload the caller broadcasts {t:'files'} on the rt data
 * channel (announce) so every end — including this one — refetches.
 */

const BASE = `${API_URL}/api/v1`

/** Mirrors the DB CHECK on rt_session_files.size_bytes (50 MB). */
export const FILE_MAX_BYTES = 50 * 1024 * 1024

export interface SessionFile {
  id: string
  filename: string
  content_type: string
  size_bytes: number
  /** presigned GET url — feed straight into <iframe src> / window.open */
  url: string
}

const EXT_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
}

/** API-accepted content type for a picked file, or null when unsupported. */
export function contentTypeFor(file: File): string | null {
  if (file.type === 'application/pdf' || file.type === 'image/png' || file.type === 'image/jpeg') {
    return file.type
  }
  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
  return EXT_TYPES[ext] ?? null
}

export interface UseSessionFilesResult {
  files: SessionFile[]
  loading: boolean
  /** last list-fetch or upload error (one at a time, friendly-tone) */
  error: string | null
  uploading: boolean
  refresh: () => void
  /** studio-only: validate → presign → PUT → complete → announce */
  upload: (file: File) => Promise<void>
}

async function apiJson<T>(url: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers)
  if (VERSION_OVERRIDE) {
    headers.set('Cloudflare-Workers-Version-Overrides', `ohear-api="${VERSION_OVERRIDE}"`)
  }
  const res = await fetch(url, { ...init, headers, credentials: 'include' })
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    /* non-JSON body */
  }
  if (!res.ok) {
    const msg =
      (body as { error?: { message?: string } } | null)?.error?.message ??
      `Request failed (${res.status})`
    throw new Error(msg)
  }
  return body as T
}

export function useSessionFiles(opts: {
  linkToken: string
  sessionId: string
  /** false in offline mock mode — no fetches, empty list */
  enabled: boolean
  /** bump (from useRtRoom) → refetch the list */
  filesVersion: number
  /** broadcast {t:'files'} after a completed upload */
  announce: () => void
}): UseSessionFilesResult {
  const { linkToken, sessionId, enabled, filesVersion, announce } = opts
  const [files, setFiles] = useState<SessionFile[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const fetchSeqRef = useRef(0)

  const refresh = useCallback(() => {
    if (!enabled) return
    const seq = ++fetchSeqRef.current
    setLoading(true)
    apiJson<{ files: SessionFile[] }>(`${BASE}/rt/join/${encodeURIComponent(linkToken)}/files`)
      .then((body) => {
        if (fetchSeqRef.current !== seq) return // a newer fetch superseded us
        setFiles(Array.isArray(body.files) ? body.files : [])
        setError(null)
      })
      .catch((err: unknown) => {
        if (fetchSeqRef.current !== seq) return
        setError(err instanceof Error ? err.message : 'Could not load session files')
      })
      .finally(() => {
        if (fetchSeqRef.current === seq) setLoading(false)
      })
  }, [enabled, linkToken])

  // initial load + refetch on every {t:'files'} notification
  useEffect(() => {
    refresh()
  }, [refresh, filesVersion])

  const upload = useCallback(
    async (file: File) => {
      setError(null)
      const contentType = contentTypeFor(file)
      if (!contentType) {
        setError('That file type is not supported — please pick a PDF, PNG or JPG.')
        return
      }
      if (file.size > FILE_MAX_BYTES) {
        setError('That file is larger than 50 MB — please upload a smaller one.')
        return
      }
      if (file.size === 0) {
        setError('That file looks empty — please pick another one.')
        return
      }
      setUploading(true)
      try {
        // 1) presign (also inserts the pending DB row)
        const presigned = await apiJson<{ file_id: string; url: string }>(
          `${BASE}/rt/sessions/${encodeURIComponent(sessionId)}/files/presign`,
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              filename: file.name,
              content_type: contentType,
              size_bytes: file.size,
            }),
          },
        )
        // 2) PUT straight to R2
        const put = await fetch(presigned.url, {
          method: 'PUT',
          headers: { 'content-type': contentType },
          body: file,
        })
        if (!put.ok) throw new Error(`Storage upload failed (${put.status})`)
        // 3) complete (upload_status → uploaded; guests can now see it)
        await apiJson(
          `${BASE}/rt/sessions/${encodeURIComponent(sessionId)}/files/${presigned.file_id}/complete`,
          { method: 'POST' },
        )
        // 4) tell everyone (bumps filesVersion locally too → our own refetch)
        announce()
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed — please try again.')
      } finally {
        setUploading(false)
      }
    },
    [announce, sessionId],
  )

  return { files, loading, error, uploading, refresh, upload }
}
